import { useState, useLayoutEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { Helmet } from 'react-helmet'
import { usePartnerConfig } from '../hooks/use-partner-config.js'
import { Hamburger } from './hamburger'
import { SideNav } from './side-nav'

import { whenSidebarFits } from '../theme'

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: black;
  transition: opacity 0.2s;
  z-index: 1;
  opacity: ${({ visible }) => (visible ? '.2' : '0')};
  display: ${({ visible }) => (visible ? 'block' : 'none')};
`

const StyledLink = styled(Link)`
  display: block;
  padding: 8px 16px;
`

const StyledAside = styled.aside`
  height: calc(100vh - 48px);
  width: 260px;
  background-color: ${({ theme }) => theme.navPrimary};
  flex-shrink: 0;
  overflow: auto;
  position: fixed;
  top: 48px;
  right: 0;
  transition: transform 0.2s cubic-bezier(0.465, 0.183, 0.153, 0.946);
  transform: translateX(${({ open }) => (open ? 0 : '100%')});
  z-index: 1;

  @media (${whenSidebarFits}) {
    height: 100vh;
    transform: translate(0);
    position: fixed;
    position: sticky; /* stylelint-disable-line declaration-block-no-duplicate-properties */
    right: initial;
    left: 0;
    top: 0;
  }
`

const TopBar = styled.div`
  width: 100vw;
  border-bottom: solid 1px ${({ theme }) => theme.border};
  z-index: 2;
  background-color: white;
  align-items: center;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  position: fixed;
  position: sticky; /* stylelint-disable-line declaration-block-no-duplicate-properties */
  top: 0;

  @media (${whenSidebarFits}) {
    display: none;
  }
`

const Menu = styled.div`
  cursor: pointer;
  fill: ${({ theme }) => theme.navBlue};
  margin: 0 16px;
`

export const SideBar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const {
    partnerConfig: { logoURL },
  } = usePartnerConfig()
  const [scrollPosition, setScrollPosition] = useState(0)
  const location = useLocation()

  useLayoutEffect(() => {
    if (!isMenuOpen && scrollPosition) {
      global.document.body.style = '' // we must make sure this is cleared or setting scrollTop won't work
      global.document.documentElement.scrollTop = scrollPosition
      setScrollPosition(0)
    }
  }, [isMenuOpen, scrollPosition])

  const handleOpenMenu = () => {
    setScrollPosition(global.document.documentElement.scrollTop)
    setIsMenuOpen(true)
  }

  const handleCloseMenu = () => {
    setIsMenuOpen(false)
  }

  const handleToggleMenu = () => {
    isMenuOpen ? handleCloseMenu() : handleOpenMenu()
  }

  // we dont render the sidebar on the full report page
  if (location.pathname.includes('print-view')) return null
  return (
    <>
      <Helmet>
        <body style={isMenuOpen ? `position: fixed; top: -${scrollPosition}px` : ''} />
      </Helmet>
      <Overlay visible={isMenuOpen} onClick={handleToggleMenu} />
      <TopBar>
        <StyledLink data-testid="ent:nav:top-bar-logo" onClick={handleCloseMenu} to="/partner/home">
          <img src={logoURL} height="25" alt="Nav" />
        </StyledLink>
        <Menu data-testid="ent:nav:top-bar-menu-toggle" isMenuOpen={isMenuOpen} onClick={handleToggleMenu}>
          <Hamburger data-testid="ent:nav:top-bar-hamburger" isOpen={isMenuOpen} />
        </Menu>
      </TopBar>
      <StyledAside open={isMenuOpen}>
        <SideNav onNavigate={handleCloseMenu} />
      </StyledAside>
    </>
  )
}
